import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/** What a layer asks before it starts closing: return `false` to keep it open (the caller may do something else instead). */
export type CloseRequest = () => boolean | void;

/**
 * Enter and exit animations for a layer that its caller mounts open and unmounts in `onClose` (P18, WS-C).
 *
 * Base UI only plays its enter phase (`data-starting-style`) when its root turns open after it mounted, and its exit
 * phase when it turns closed while still mounted. So the layer keeps its own `open`: false on the first render, true
 * once committed (enter), false again on a close request (Escape, backdrop, the close button, a swipe, a footer button).
 * The caller's `onClose` then runs from `onOpenChangeComplete(false)`, after the exit animation, which is when the
 * caller unmounts the layer exactly as before. Focus goes back to the trigger at the same point. Under reduced motion,
 * or where nothing animates (happy-dom), completion comes about a frame later.
 *
 * - `onCloseRequest`: called first on every request; `false` keeps the layer open (a running import aborts instead).
 * - `animate: false`: mounted open, `request()` calls `onClose` at once (a layer that is unmounted with its owner's state
 *   and has no exit, like the phone's properties region).
 *
 * `onClose` is read when the close is *requested*, not when it completes: a close asked for one target (a selection, a
 * preview) still closes that one if the caller's props change during the 140 to 224 ms of the exit.
 */
export function useDeferredClose(
  onClose: () => void,
  options: { onCloseRequest?: CloseRequest; animate?: boolean } = {},
) {
  const animate = options.animate ?? true;
  const [open, setOpen] = useState(!animate);
  useEffect(() => {
    if (animate) setOpen(true);
  }, [animate]);

  const latest = useRef({ onClose, onCloseRequest: options.onCloseRequest, animate });
  latest.current = { onClose, onCloseRequest: options.onCloseRequest, animate };
  const requested = useRef<(() => void) | null>(null);

  /** Starts closing. Returns false when `onCloseRequest` refused (the layer stays open), true otherwise. */
  const request = useCallback((): boolean => {
    if (requested.current) return true;
    const { onClose: close, onCloseRequest: ask, animate: animated } = latest.current;
    if (ask?.() === false) return false;
    if (!animated) {
      close();
      return true;
    }
    requested.current = close;
    setOpen(false);
    return true;
  }, []);

  /** Base UI's `onOpenChangeComplete`: the enter and the exit are over. */
  const complete = useCallback((isOpen: boolean) => {
    if (isOpen) return;
    const close = requested.current;
    requested.current = null;
    close?.();
  }, []);

  return { open, request, complete };
}

const DialogCloseContext = createContext<(() => boolean) | null>(null);
export const DialogCloseProvider = DialogCloseContext.Provider;

/**
 * Closes the `Dialog`, `Sheet`, `Panel`, `Drawer` or `AdaptiveDialog` around the caller, with its exit animation:
 * the function to give a footer button that cancels or closes (`onClick={useDialogClose()}`), or use `DialogClose`.
 * `onClose` of the layer runs when the exit is over; its `onCloseRequest` can still refuse.
 */
export function useDialogClose(): () => void {
  const request = useContext(DialogCloseContext);
  if (!request)
    throw new Error(
      'useDialogClose must be used inside a Dialog, Sheet, Panel, Drawer or AdaptiveDialog',
    );
  return useCallback(() => void request(), [request]);
}
